import { Image, Paperclip } from "lucide-react";
import type React from "react";
import { memo, useCallback, useRef } from "react";
import { RedoButton } from "../../arbiter-components/buttons/redo-button";
import { DropdownMenuItem } from "../ui/dropdown-menu";
import { useToolbarOverflow } from "./toolbar-overflow";

interface ImageToolbarButtonProps {
  onImageSelect?: (file: File) => void;
}

export const ImageToolbarButton = memo(function ImageToolbarButton({
  onImageSelect,
}: ImageToolbarButtonProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const { isOverflow } = useToolbarOverflow();

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) {
        return;
      }
      onImageSelect?.(file);
      e.target.value = "";
    },
    [onImageSelect],
  );

  return (
    <>
      {isOverflow ? (
        <DropdownMenuItem onSelect={() => inputRef.current?.click()}>
          <Image />
          Image
        </DropdownMenuItem>
      ) : (
        <RedoButton
          hierarchy="tertiary"
          IconLeading={Image}
          onClick={() => inputRef.current?.click()}
          size="sm"
        />
      )}
      <input
        accept="image/*"
        className="hidden"
        onChange={handleFileChange}
        ref={inputRef}
        type="file"
      />
    </>
  );
});

interface FileToolbarButtonProps {
  onFileSelect?: (files: File[]) => void;
}

export const FileToolbarButton = memo(function FileToolbarButton({
  onFileSelect,
}: FileToolbarButtonProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const { isOverflow } = useToolbarOverflow();

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (!files || files.length === 0) {
        return;
      }
      onFileSelect?.([...files]);
      e.target.value = "";
    },
    [onFileSelect],
  );

  return (
    <>
      {isOverflow ? (
        <DropdownMenuItem onSelect={() => inputRef.current?.click()}>
          <Paperclip />
          Attach file
        </DropdownMenuItem>
      ) : (
        <RedoButton
          hierarchy="tertiary"
          IconLeading={Paperclip}
          onClick={() => inputRef.current?.click()}
          size="sm"
        />
      )}
      <input
        className="hidden"
        multiple
        onChange={handleFileChange}
        ref={inputRef}
        type="file"
      />
    </>
  );
});
